import { useFormContext } from 'react-hook-form';
import type { Control, FieldErrors } from 'react-hook-form';
import type { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';
import type { QuizEditFormStateType } from 'types/page/quizEditFormStateType';
import type { FormEditFormStateType } from 'types/page/formEditFormStateType';
import type { VoiceChatEditFormStateType } from 'types/page/voiceChatEditFormStateType';

import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import FormWysiwygEditor from 'components/form/FormWysiwygEditor';
import { DivConst } from 'constants/divConst';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { getDivLabel, getDivSelectOptions } from 'utils/app';

/**
 * respondentFields 要素の共通構造（quiz/diagnosis/form/voiceChat の 4 種で完全一致）。
 */
type RespondentFieldItem = {
  getFlag: boolean;
  respondentFieldDiv: number;
  respondentStandardFieldDiv: number;
  label: string;
  placeholder: string;
  respondentCustomFieldDiv: number;
  respondentCustomFieldInputDiv: number;
  maxLength: number;
};

/**
 * WsContentEditRespondentFields が実際に使う useFieldArray の操作（move / fields）のみを持つ最小インターフェース。
 * UseFieldArrayReturn<T, 'respondentFields', 'id'> を union 型でそのまま受けると、
 * TS の FieldArrayPath<T> がunionメンバー全体の構造一致を要求し、
 * results の有無が異なる VoiceChatEditFormStateType 追加時に既存 3 画面側で型エラーになるため、
 * 必要な操作だけを持つ独立型に切り出して構造的部分型で受け取る。
 */
export type RespondentFieldArrayLike = {
  move: (from: number, to: number) => void;
  fields: (RespondentFieldItem & { id: string })[];
};

/**
 * コンテンツ作成編集 取得項目設定コンポーネント
 */
export default function WsContentEditRespondentFields(props: {
  readOnly: boolean;
  contentCategory: { label: string; value: number };
  respondentFieldArray: RespondentFieldArrayLike;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const formContext = useFormContext();
  let typedFormContext;
  if (props.contentCategory.label === 'クイズ') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<QuizEditFormStateType>
    >;
  } else if (props.contentCategory.label === '診断') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<DiagnosisEditFormStateType>
    >;
  } else if (props.contentCategory.label === 'フォーム') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<FormEditFormStateType>
    >;
  } else if (props.contentCategory.label === 'ボイスチャット') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<VoiceChatEditFormStateType>
    >;
  } else {
    throw new Error('無効なコンテンツカテゴリ');
  }
  const {
    control,
    register,
    setValue,
    formState: { errors },
    watch,
    clearErrors,
    setError,
  } = typedFormContext;

  const { move, fields } = props.respondentFieldArray;

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const [initialized, setInitialized] = useState(false);
  const respondentMail =
    {
      クイズ: getDivSelectOptions('quizRespondentMail'),
      診断: getDivSelectOptions('diagnosisRespondentMail'),
      フォーム: getDivSelectOptions('formRespondentMail'),
      ボイスチャット: getDivSelectOptions('voiceChatRespondentMail'),
    }[props.contentCategory.label] || [];
  const reportMailDescribeHtmlPaceHolder =
    {
      クイズ:
        '（例）あなたの回答結果と本クイズの解説をクイズ完了後にメールでお送りいたします。※メールアドレス登録後にクイズが開始されます。',
      診断: '（例）診断結果と詳細なフィードバックを診断完了後にメールでもお送りします。',
      フォーム: '（例）フォーム回答結果と詳細なフィードバックを回答完了後にメールでお送りします。',
    }[props.contentCategory.label] || '';

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 初期化時の取得項目を取得します。
   */
  const _getDefaultFields = useCallback(() => {
    // 標準項目
    const respondentStandardFields = getDivSelectOptions('respondentStandardFieldDiv').map(
      (item, index) => ({
        no: index + 1,
        // メールアドレスはデフォルトで取得
        getFlag: Number(item.value) === DivConst.items.respondentStandardFieldDiv.mailAddress.value,
        respondentFieldDiv: DivConst.items.respondentFieldDiv.standard.value,
        respondentStandardFieldDiv: Number(item.value),
        label: '',
        placeholder: '',
        respondentCustomFieldDiv: 0,
        respondentCustomFieldInputDiv: 0,
        maxLength: 0,
      })
    );
    // 自由項目
    const respondentCustomFields = getDivSelectOptions('respondentCustomFieldDiv').map(
      (item, index) => ({
        no: respondentStandardFields.length + index + 1,
        getFlag: false,
        respondentFieldDiv: DivConst.items.respondentFieldDiv.custom.value,
        respondentStandardFieldDiv: 0,
        label: '',
        placeholder: '',
        respondentCustomFieldDiv: Number(item.value),
        respondentCustomFieldInputDiv: DivConst.items.respondentCustomFieldInputDiv.text.value,
        maxLength: DivConst.items.respondentCustomFieldMaxLength.maxLength20.value,
      })
    );
    return [...respondentStandardFields, ...respondentCustomFields];
  }, []);

  /**
   * 画面の状態を初期化します。
   */
  const _initState = useCallback(async () => {
    if (fields.length === 0) {
      setValue('respondentFields', _getDefaultFields());
    }
  }, [fields.length, setValue, _getDefaultFields]);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        await _initState();
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
  }, [_initState, showBoundary]);

  // respondentMailDivとgetFlagの変更を監視
  const respondentMailDiv = watch('respondentMailDiv');
  const respondentFields = watch('respondentFields');

  useEffect(() => {
    // 項目取得なしの場合はエラーをクリア
    if (
      Number(respondentMailDiv) === DivConst.items.quizRespondentMail.none.value ||
      Number(respondentMailDiv) === DivConst.items.diagnosisRespondentMail.none.value ||
      Number(respondentMailDiv) === DivConst.items.voiceChatRespondentMail.none.value
    ) {
      clearErrors('respondentMailDiv');
      return;
    }

    // 少なくとも1つの項目が取得対象になっているかチェック
    const hasGetFlag = respondentFields?.some(field => field.getFlag);
    if (!hasGetFlag) {
      setError('respondentMailDiv', {
        type: 'custom',
        message: '回答者情報の取得項目を 1 件以上選択してください',
      });
    } else {
      clearErrors('respondentMailDiv');
    }
  }, [respondentMailDiv, respondentFields, clearErrors, setError]);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  const onMove = (fieldIndex: number, upper: boolean) => {
    // 先頭の場合は上に移動不可
    if (upper && fieldIndex === 0) {
      return;
    }
    // 最終行の場合は下に移動不可
    if (!upper && fieldIndex === fields.length - 1) {
      return;
    }
    // 要素の入れ替え
    move(fieldIndex, upper ? fieldIndex - 1 : fieldIndex + 1);
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* 初期化中の表示 */}
      <IndicatorScreen visible={!initialized} />
      {/* 初期化後の表示 */}
      {initialized && (
        <section className="section-form-default">
          <div className="container">
            <div className="box-common shadow-none">
              <h3 className="title-section title-md">取得項目設定</h3>

              <div className="form-group">
                <FormSelect
                  subject="項目取得タイミング"
                  name="respondentMailDiv"
                  register={register}
                  errors={errors}
                  options={respondentMail}
                  disabled={props.readOnly}
                />
              </div>
              {props.contentCategory.label !== 'ボイスチャット' && (
                <div className="form-group">
                  <FormWysiwygEditor
                    control={control as unknown as Control<FormEditFormStateType>}
                    subject="メール取得時の説明文"
                    name="reportMailDescribeHtml"
                    placeholder={reportMailDescribeHtmlPaceHolder}
                    toolbar={['highlight']}
                    errors={errors as unknown as FieldErrors<FormEditFormStateType>}
                    readOnly={props.readOnly}
                  />
                </div>
              )}
              <div className="form-group">
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '120px' }}>順序</th>
                        <th style={{ width: '80px' }}>取得</th>
                        <th style={{ width: '100%' }}>項目</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((item, index) => (
                        <tr key={item.id}>
                          {/* 順序 */}
                          <td>
                            <div className="btn-group-header">
                              <div className="btn-group">
                                <button
                                  className="btn btn-sm"
                                  onClick={() => onMove(index, true)}
                                  disabled={props.readOnly}
                                >
                                  <img src="/img/icon/arrow-up-black.svg" alt="" />
                                </button>
                                <button
                                  className="btn btn-sm"
                                  onClick={() => onMove(index, false)}
                                  disabled={props.readOnly}
                                >
                                  <img
                                    src="/img/icon/arrow-up-black.svg"
                                    alt=""
                                    className="scaley-1"
                                  />
                                </button>
                              </div>
                            </div>
                          </td>
                          {/* 取得フラグ */}
                          <td className="text-center">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                disabled={props.readOnly}
                                {...register(`respondentFields.${index}.getFlag`)}
                              />
                            </div>
                          </td>
                          {/* 項目 */}
                          <td>
                            {/* 標準項目 */}
                            {item.respondentFieldDiv ===
                              DivConst.items.respondentFieldDiv.standard.value &&
                              getDivLabel(
                                'respondentStandardFieldDiv',
                                item.respondentStandardFieldDiv
                              )}
                            {/* 自由項目 */}
                            {item.respondentFieldDiv ===
                              DivConst.items.respondentFieldDiv.custom.value && (
                              <div className="d-flex flex-column gap-3">
                                <div className="d-flex gap-3">
                                  {/* 項目名 */}
                                  <div className="col-auto col-form-label">
                                    {getDivLabel(
                                      'respondentCustomFieldDiv',
                                      item.respondentCustomFieldDiv
                                    )}
                                  </div>
                                  {/* 質問文 */}
                                  <div className="flex-grow-1">
                                    <FormText
                                      name={`respondentFields.${index}.label`}
                                      required={watch(`respondentFields.${index}.getFlag`)}
                                      register={register}
                                      errors={errors}
                                      readOnly={props.readOnly}
                                      placeholder="質問文（例）あなたの〇〇を教えてください。"
                                      className="w-100"
                                    />
                                  </div>
                                  {/* プレースホルダー */}
                                  <div className="flex-grow-1">
                                    <FormText
                                      name={`respondentFields.${index}.placeholder`}
                                      register={register}
                                      errors={errors}
                                      readOnly={props.readOnly}
                                      placeholder="プレースホルダー"
                                      className="w-100"
                                    />
                                  </div>
                                </div>
                                <div className="d-flex align-items-center">
                                  {/* 入力タイプ */}
                                  <fieldset className="mb-0 p-0">
                                    <div className="form-check form-check-inline">
                                      <input
                                        type="radio"
                                        value={
                                          DivConst.items.respondentCustomFieldInputDiv.text.value
                                        }
                                        {...register(
                                          `respondentFields.${index}.respondentCustomFieldInputDiv`
                                        )}
                                        readOnly={props.readOnly}
                                        id={`textField-${index}`}
                                        className="form-check-input"
                                        defaultChecked={
                                          item.respondentCustomFieldInputDiv ===
                                          DivConst.items.respondentCustomFieldInputDiv.text.value
                                        }
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`textField-${index}`}
                                      >
                                        文字
                                      </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                      <input
                                        type="radio"
                                        value={
                                          DivConst.items.respondentCustomFieldInputDiv.numeric.value
                                        }
                                        {...register(
                                          `respondentFields.${index}.respondentCustomFieldInputDiv`
                                        )}
                                        readOnly={props.readOnly}
                                        id={`numberField-${index}`}
                                        className="form-check-input"
                                        defaultChecked={
                                          item.respondentCustomFieldInputDiv ===
                                          DivConst.items.respondentCustomFieldInputDiv.numeric.value
                                        }
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`numberField-${index}`}
                                      >
                                        数字
                                      </label>
                                    </div>
                                  </fieldset>
                                  {/* 文字数 */}
                                  <div className="d-flex align-items-center">
                                    <label className="form-label mb-0 me-2">文字数</label>
                                    <FormSelect
                                      name={`respondentFields.${index}.maxLength`}
                                      register={register}
                                      errors={errors}
                                      disabled={props.readOnly}
                                      options={getDivSelectOptions(
                                        'respondentCustomFieldMaxLength'
                                      )}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
